/**
 * RoomCard — Hotel room card component for the hotels flavor.
 *
 * Uses the SAME CSS class structure as ItemCard (.ttp-product-card, etc.)
 * so it inherits the existing ecommerce layout (horizontal scroll, 140px cards).
 * Only the button label ("Book Now") and stepper unit ("nights") differ.
 *
 * Options:
 *   room: { id, name, imageUrl, subtitle, pricePerNight, currency, available, nights }
 *   compact: boolean (list vs card mode — hotels always use card mode on desktop)
 *   primaryColor: hex string
 *   onBook: (cardEl, room) => void
 */
export class RoomCard {
  constructor(room, options = {}) {
    this.room = room;
    this.compact = options.compact || false;
    this.primaryColor = options.primaryColor || '#2563eb';
    this.onBook = options.onBook || (() => {});
    this.element = null;
    this.nights = room.nights || 1;
  }

  render() {
    const el = document.createElement('div');
    el.className = this.compact ? 'ttp-product-compact' : 'ttp-product-card';

    const r = this.room;
    const sym = r.currency === 'ILS' ? '\u20AA' : (r.currency === 'EUR' ? '\u20AC' : '$');

    if (this.compact) {
      el.innerHTML = `
        <div class="ttp-product-image-sm">
          <img src="${r.imageUrl}" alt="${r.name}" onerror="this.style.display='none'" />
        </div>
        <div class="ttp-product-info">
          <div class="ttp-product-name">${r.name}</div>
          <div class="ttp-product-meta">${r.subtitle || ''}</div>
        </div>
        <div class="ttp-product-action">
          <div class="ttp-product-price">${sym}${r.pricePerNight}<span class="ttp-room-per-night">/night</span></div>
          ${r.available !== false
            ? '<button class="ttp-add-btn">Book</button>'
            : '<span class="ttp-out-of-stock">Unavailable</span>'}
          <div class="ttp-stepper" data-sell-by="nights">
            <button class="ttp-stepper-btn ttp-stepper-minus" type="button">\u2212</button>
            <span class="ttp-stepper-value">${this.nights} night${this.nights !== 1 ? 's' : ''}</span>
            <button class="ttp-stepper-btn ttp-stepper-plus" type="button">+</button>
          </div>
        </div>
      `;
    } else {
      const total = (r.pricePerNight * this.nights).toFixed(0);
      const amenities = r.amenities || [];
      const maxShow = 3;
      const shown = amenities.slice(0, maxShow);
      const extra = amenities.length - maxShow;
      const amenityHtml = shown.length > 0
        ? `<div class="ttp-room-amenities">${shown.map(a => `<span class="ttp-amenity-tag">${a}</span>`).join('')}${extra > 0 ? `<span class="ttp-amenity-tag ttp-amenity-more">+${extra}</span>` : ''}</div>`
        : '';

      el.innerHTML = `
        <div class="ttp-product-image">
          <img src="${r.imageUrl}" alt="${r.name}" onerror="this.style.display='none'" />
          ${r.available === false ? '<div class="ttp-stock-badge">Unavailable</div>' : ''}
          ${amenityHtml}
        </div>
        <div class="ttp-product-details">
          <div class="ttp-product-name">${r.name}</div>
          <div class="ttp-product-meta">${sym}${r.pricePerNight}/night</div>
          <div class="ttp-product-footer">
            <div class="ttp-stepper" data-sell-by="nights">
              <button class="ttp-stepper-btn ttp-stepper-minus" type="button">\u2212</button>
              <span class="ttp-stepper-value">${this.nights}n</span>
              <button class="ttp-stepper-btn ttp-stepper-plus" type="button">+</button>
            </div>
            ${r.available !== false
              ? `<button class="ttp-add-btn">Book \u00B7 ${sym}${total}</button>`
              : '<span class="ttp-out-of-stock">Unavailable</span>'}
          </div>
        </div>
      `;
    }

    this._bindEvents(el, sym);
    this.element = el;
    return el;
  }

  _bindEvents(el, sym) {
    const minusBtn = el.querySelector('.ttp-stepper-minus');
    const plusBtn = el.querySelector('.ttp-stepper-plus');
    const valueDisplay = el.querySelector('.ttp-stepper-value');
    const bookBtn = el.querySelector('.ttp-add-btn');

    if (minusBtn && plusBtn && valueDisplay) {
      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.nights > 1) {
          this.nights--;
          this._updateDisplay(valueDisplay, minusBtn, bookBtn, sym);
        }
      });

      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.nights < 30) {
          this.nights++;
          this._updateDisplay(valueDisplay, minusBtn, bookBtn, sym);
        }
      });

      minusBtn.disabled = this.nights <= 1;
      minusBtn.classList.toggle('ttp-stepper-disabled', this.nights <= 1);
    }

    if (bookBtn) {
      bookBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        bookBtn.textContent = '\u2713 Booked!';
        bookBtn.classList.add('ttp-added');
        bookBtn.disabled = true;

        this.onBook(el, {
          ...this.room,
          selectedNights: this.nights
        });
      });
    }
  }

  _updateDisplay(valueDisplay, minusBtn, bookBtn, sym) {
    valueDisplay.textContent = this.compact
      ? `${this.nights} night${this.nights !== 1 ? 's' : ''}`
      : `${this.nights}n`;
    minusBtn.disabled = this.nights <= 1;
    minusBtn.classList.toggle('ttp-stepper-disabled', this.nights <= 1);

    if (bookBtn && !bookBtn.disabled) {
      const total = (this.room.pricePerNight * this.nights).toFixed(0);
      bookBtn.textContent = `Book \u00B7 ${sym}${total}`;
    }
  }
}
